import Tea from 'byted-tea-sdk';
import { isDev, isProd, ENABLE_TEA, TEA_APP_ID_VOLC, TEA_CHANNEL_VOLC } from '@/config';
import Utils from '@/utils';
import { SceneType } from '@/store/slices/scene';

const TEA_ENV = isProd ? 'prod' : isDev ? 'dev' : 'unknown';

export interface LoginInfo {
  user_id: string;
  user_name: string;
  room_id: string;
  device_id: string;
}

export const enum TeaEvents {
  USER_LOGIN = 'user_login',
  USER_LOGOUT = 'user_logout',
  USER_CHOOSE_SCENE = 'user_choose_scene',
  JOIN_ROOM = 'join_room',
  LEAVE_ROOM = 'leave_room',
  START_SHARING = 'start_sharing',
  STOP_SHARING = 'stop_sharing',
  MUTE_ALL = 'mute_all',
  RTC_SDK_API_CALL_FAILURE = 'rtc_sdk_api_call_failure',
  RTC_SDK_EXCEPTION = 'rtc_sdk_exception',
  DEMO_EXCEPTION = 'demo_exception',
  SEND_SERVER_MESSAGE = 'send_server_message',
  RECV_SERVER_MESSAGE = 'recv_server_message',
}

export class TeaClient {
  private _reportId = 0;

  private _userId?: string;

  private _userName?: string;

  private _deivceId?: string;

  private _roomId?: string;

  private _sceneType?: string;

  constructor() {
    Tea.init({
      app_id: TEA_APP_ID_VOLC,
      channel: TEA_CHANNEL_VOLC,
      log: true,
    });

    this._deivceId = Utils.getDeviceId();
    Tea.start();
  }

  setLoginInfo = (userName: string, userId: string, device_id: string) => {
    this._userName = userName;
    this._userId = userId;
    this._deivceId = device_id;
  };

  setJoinRoomInfo = (roomId: string) => {
    this._roomId = roomId;
  };

  setSceneType = (sceneType: SceneType) => {
    this._sceneType = sceneType || 'unchosen';
  };

  // 每次事件上报共同的信息
  _getCommonTeaBeaconInfo = () => {
    return {
      timestamp: Date.now(),
      report_id: this._reportId++,
      user_id: this._userId,
      user_name: this._userName,
      room_id: this._roomId,
      device_id: this._deivceId,
      scene: this._sceneType,
      env: TEA_ENV,
    };
  };

  // 适用于正常事件, 进入队列，按照一定间隔转发
  sendTeaBeacon = (eventName: TeaEvents, paramsJson: object) => {
    if (ENABLE_TEA && typeof Tea.event === 'function') {
      const commonReports = this._getCommonTeaBeaconInfo();
      Tea.event(eventName, {
        ...paramsJson,
        ...commonReports,
      });
    }
  };

  // 适用于用户离开页面, 及时转发
  sendTeaBeaconImmediately = (eventName: TeaEvents, paramsJson: object) => {
    if (ENABLE_TEA && typeof Tea.beconEvent === 'function') {
      const commonReports = this._getCommonTeaBeaconInfo();
      Tea.beconEvent(eventName, {
        ...paramsJson,
        ...commonReports,
      });
    }
  };

  // SDK API 调用失败数据上报
  reportRTCSdkAPIFailure = (apiName: string, message: string) => {
    this.sendTeaBeacon(TeaEvents.RTC_SDK_API_CALL_FAILURE, {
      sdk_api_name: apiName,
      message,
    });
  };

  // SDK API 调用异常数据上报
  reportRTCSdkException = (message: string) => {
    this.sendTeaBeacon(TeaEvents.RTC_SDK_EXCEPTION, {
      message,
    });
  };

  // Demo 内部异常数据上报
  reportDemoInternalException = (message: string) => {
    this.sendTeaBeacon(TeaEvents.DEMO_EXCEPTION, {
      message,
    });
  };

  // 用户登录
  reportUserLogin = (res: boolean, reason?: string | '') => {
    this.sendTeaBeacon(TeaEvents.USER_LOGIN, {
      res,
      reason,
    });
  };

  // 用户登出
  reportUserLogout = () => {
    this.sendTeaBeacon(TeaEvents.USER_LOGOUT, {});
  };

  // 用户选择场景
  reportUserChooseScene = () => {
    this.sendTeaBeacon(TeaEvents.USER_CHOOSE_SCENE, {});
  };

  // 进房
  reportUserJoinRoom = (res: boolean, reason?: string | '') => {
    this.sendTeaBeacon(TeaEvents.JOIN_ROOM, {
      res,
      reason,
    });
  };

  // 离房
  reportUserLeaveRoom = () => {
    this.sendTeaBeacon(TeaEvents.LEAVE_ROOM, {});
  };

  // 用户开始共享
  reportStartSharing = (type: 'screen' | 'board') => {
    this.sendTeaBeacon(TeaEvents.START_SHARING, {
      type,
    });
  };

  // 用户点击结束共享
  reportStopSharing = (type: 'screen' | 'board') => {
    this.sendTeaBeacon(TeaEvents.STOP_SHARING, {
      type,
    });
  };
}

export default new TeaClient();
